// @ts-strict-ignore
/**
 * Resource backed by a network API client supporting standard
 * CRUD operations.
 */

import isObject from 'lodash/isObject';
import isArray from 'lodash/isArray';
import mapValues from 'lodash/mapValues';

import {reporter} from 'common/report/error-reporting';
import {SubscribableContext} from 'common/util/model-resource/subscribable-context';

import {ResponseError} from '../shared-client';
import {Model, ModelStatus} from './model-resource';

export interface ApiResourceClient<ModelValue> {
  index: () => {
    /**
     * This should return null if there is no result in the cache. Otherwise isLoadingAll() will be unreliable.
     */
    cache: Promise<{[key: string]: ModelValue}>;
    network: Promise<{[key: string]: ModelValue}>;
  };
  get?: (id: string) => {
    cache: Promise<ModelValue>;
    network: Promise<ModelValue>;
  };
  purgeCache: (id?: string) => void;
}

const INDEX_FETCH_ID = `idx` as const;

//** Map where a key of 'idx' holds a different value than other string keys */
interface FetchesInProgressMap<ModelValue> extends Map<`idx` | string, any> {
  get(key: typeof INDEX_FETCH_ID): ReturnType<ApiResourceClient<ModelValue>[`index`]> | undefined;
  get(key: string): {cache: Promise<ModelValue>; network: Promise<ModelValue>} | undefined;
  set(key: typeof INDEX_FETCH_ID, value: ReturnType<ApiResourceClient<ModelValue>[`index`]>): this;
  set(key: string, value: {cache: Promise<ModelValue>; network: Promise<ModelValue>}): this;
}

function canDoPartialUpdate(value) {
  return isObject(value) && !isArray(value);
}

// helper to apply potentially-nested object updates
// without killing other fields
// TODO extract and test
export function applyPartialUpdates(value, update) {
  if (canDoPartialUpdate(value) && canDoPartialUpdate(update)) {
    for (const [k, v] of Object.entries(update)) {
      value[k] = applyPartialUpdates(value[k], v);
    }
  } else {
    value = update;
  }
  return value;
}

export enum ResourceStatus {
  /** Index fetch not started */
  Idle = `Idle`,
  /** Index fetch in progress */
  Loading = `Loading`,
  /** Last Index fetch succeeded */
  Success = `Success`,
  /** Last Index fetch failed */
  Error = `Error`,
}

export class ApiResource<ModelValue> extends SubscribableContext {
  protected declare apiClient: ApiResourceClient<ModelValue>;
  protected fetchesInProgress: FetchesInProgressMap<ModelValue> = new Map();
  protected models: Map<string, Model<ModelValue>> = null;
  private resourceStatus: ResourceStatus = ResourceStatus.Idle;

  getOne(id: string | number): Model<ModelValue> {
    let model: Model<ModelValue> = null;
    id = String(id);
    if (this.models === null) {
      this.fetchIndex();
      model = this.loadingModel();
    } else if (this.models.has(id)) {
      model = this.models.get(id);
    } else if (
      this.resourceStatus === ResourceStatus.Loading ||
      this.isLoadingAll() ||
      this.fetchesInProgress.has(id)
    ) {
      model = this.loadingModel();
    } else {
      model = this.notFoundModel();
    }
    return model;
  }

  getAll(refresh = false): Map<string, Model<ModelValue>> {
    let models;
    if (this.models === null || refresh) {
      this.fetchIndex();
      models = new Map();
    } else {
      models = this.models;
    }
    return models;
  }

  getAllValues(): ModelValue[] {
    // exclude error state models (there should only be one), as they won't have a model value
    return Array.from(this.getAll().values())
      .filter((model) => !!model.value)
      .map((model) => model.value);
  }

  get status(): ResourceStatus {
    return this.resourceStatus;
  }

  get count(): number {
    return this.models.size;
  }

  /**
   * Checks if all models are currently loading.
   * Note: if this isn't doing what you think it should, check the resource client: it should return null for an empty cache.
   */
  isLoadingAll(): boolean {
    return this.fetchesInProgress.has(INDEX_FETCH_ID);
  }

  /**
   * Ensures an index fetch has happened: waits for any in-progress index fetch to complete, or
   * starts one if it hasn't been done already
   */
  async waitForIndexFetch(): Promise<void> {
    const fetchInProgress = this.fetchesInProgress.get(INDEX_FETCH_ID);
    if (fetchInProgress) {
      const {cache, network} = fetchInProgress;
      await Promise.all([cache, network]);
    } else {
      if (this.models) {
        // already fetched
        return Promise.resolve();
      }
      return this.fetchIndex();
    }
  }

  // TODO support network update or not (configurable by the particular apiClient
  // and/or by caller?)
  update(
    updates: {[key: string]: Partial<ModelValue> | null | undefined},
    {partialUpdate = true}: {partialUpdate?: boolean} = {},
  ) {
    const models = this.models ?? new Map();
    for (const [id, update] of Object.entries(updates)) {
      let currentModel: Model<ModelValue> = models.get(String(id));
      if (!currentModel) {
        currentModel = {value: update as ModelValue, status: ModelStatus.Success};
        models.set(id, currentModel);
      } else {
        currentModel.value = partialUpdate ? applyPartialUpdates(currentModel.value, update) : update;
      }
    }
    this.models = models;
    this.apiClient.purgeCache();
    this.notifySubscribers();
  }

  /** Removes the model from the resource; does not make an API call */
  delete(id: string | number) {
    if (this.models.delete(String(id))) {
      this.apiClient.purgeCache();
      this.notifySubscribers();
    }
  }

  bulkDeleteModels(ids: string | number[]) {
    let deletedAny = false;
    for (const id of ids) {
      deletedAny = this.models.delete(String(id));
    }
    if (deletedAny) {
      this.apiClient.purgeCache();
      this.notifySubscribers();
    }
  }

  private setModels(modelValues, notify: boolean) {
    if (!modelValues) {
      return;
    }

    this.models = new Map(
      Object.entries(mapValues(modelValues, (value): Model<ModelValue> => ({value, status: ModelStatus.Success}))),
    );

    this.resourceStatus = ResourceStatus.Success;

    // avoid superfluous repeated notifications by only
    // notifying in the call where we actually made the
    // API call or looked in browser cache
    if (notify) {
      this.notifySubscribers();
    }
  }

  private setOneModel(id, value, notify: boolean) {
    // TODO do we want to support null values? If so, fromFetchCache stuff
    // in SharedClient needs reworking
    if (value === null) {
      return;
    }

    this.models = this.models ?? new Map();
    const model: Model<ModelValue> =
      typeof value === `undefined` ? this.notFoundModel() : {value, status: ModelStatus.Success};
    this.models.set(id, model);
    this.resourceStatus = ResourceStatus.Success;
    this.fetchesInProgress.delete(id);

    // avoid superfluous repeated notifications by only
    // notifying in the call where we actually made the
    // API call or looked in browser cache
    if (notify) {
      this.notifySubscribers();
    }
  }

  private async fetchIndex() {
    let initiatedFetch = false;
    if (!this.fetchesInProgress.has(INDEX_FETCH_ID)) {
      this.fetchesInProgress.set(INDEX_FETCH_ID, this.apiClient.index());
      this.resourceStatus = ResourceStatus.Loading;
      initiatedFetch = true;

      // allow components to render loading state
      this.notifySubscribers();
    }

    const {cache, network} = this.fetchesInProgress.get(INDEX_FETCH_ID);

    await Promise.all([
      cache.then((modelValues) => this.setModels(modelValues, initiatedFetch)),
      network
        .then((modelValues) => {
          this.setModels(modelValues, initiatedFetch);
        })
        .catch((error) => this.handleFetchError(error, INDEX_FETCH_ID, initiatedFetch)),
    ]);
    this.fetchesInProgress.delete(INDEX_FETCH_ID);
  }

  // handleFetchError assumes that the apiClient is using the shared HttpClient, and thus we get a ResponseError here
  handleFetchError(error: ResponseError, fetchId: string, notify: boolean): any {
    reporter.error(error.message, error);
    if (fetchId === INDEX_FETCH_ID) {
      this.resourceStatus = ResourceStatus.Error;
      // if we never set this.models, clients will keep causing index fetch to be requested over and over
      this.models = new Map();
    }
    this.fetchesInProgress.delete(fetchId);
    if (notify) {
      this.notifySubscribers();
    }
  }

  protected async fetchOne(id: string) {
    let initiatedFetch = false;

    if (!this.fetchesInProgress.has(id)) {
      this.fetchesInProgress.set(id, this.apiClient.get(id));
      initiatedFetch = true;
      this.resourceStatus = ResourceStatus.Loading;
    }

    const {cache, network} = this.fetchesInProgress.get(id);
    cache.then((modelValue) => this.setOneModel(id, modelValue, initiatedFetch));
    network.then((modelValue) => this.setOneModel(id, modelValue, initiatedFetch));

    await Promise.all([cache, network]);
    this.fetchesInProgress.delete(id);
  }

  protected loadingModel(): Model<ModelValue> {
    return {status: ModelStatus.Loading};
  }

  protected notFoundModel(): Model<ModelValue> {
    return {status: ModelStatus.NotFound};
  }
}

export class ApiSingleValueResource<ModelValue> extends ApiResource<ModelValue> {
  public id: string = null;

  get modelKey() {
    return String(this.id);
  }

  get(): Model<ModelValue> {
    let model = null;

    // Early calls to `get` for public dashboards can get into a race condition
    // where a lot of the state is not yet initialized. This check avoids erroring out
    // if the state is not yet ready for fetching, loading or getting the model.
    if (this.models === null && !this.modelKey) {
      return this.notFoundModel();
    }

    if (this.models === null || !this.models.has(this.modelKey)) {
      this.fetchOne(this.modelKey);
      model = this.loadingModel();
    } else if (this.fetchesInProgress.has(this.modelKey)) {
      model = this.loadingModel();
    } else {
      model = this.models.get(this.modelKey);
    }
    return model;
  }

  // TODO support network update or not (configurable by the particular apiClient
  // and/or by caller?)
  update(update) {
    const models = this.models || new Map();
    let currentModel: Model<ModelValue> = models.get(this.modelKey);
    if (!currentModel) {
      currentModel = {value: update, status: ModelStatus.Success};
      models.set(this.modelKey, currentModel);
    } else {
      currentModel.value = applyPartialUpdates(currentModel.value, update);
    }
    this.models = models;
    this.apiClient.purgeCache(this.modelKey);
    this.notifySubscribers();
  }
}
